import { Box, Card, CardContent, Icon, IconButton, InputAdornment, Tooltip } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDInput from "components/MDInput";
import OfferCustomSelect from "./OfferCustomSelect";
import { useNavigate } from "react-router-dom";

interface Offer {
  brokerIso: string | null;
  salesRep: string | null;
  internalIsoRep: string | null;
  funder: string | null;
}

interface Filters {
  fromDate: Date | null;
  toDate: Date | null;
  brokerIso: string | null;
  salesRep: string | null;
  internalRep: string | null;
  funder: string | null;
  searchTerm: string;
  status: any;
}

interface FilterComponentsProps {
  offers: Offer[];
  filters: Filters;
  selectedOffer: any;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  setApplicationFilters: React.Dispatch<React.SetStateAction<any>>;
  onFilterChange: (key: keyof Filters, value: any) => void;
  onEditOfferClick: (offer: any) => void;
}

const FilterComponents: React.FC<FilterComponentsProps> = ({
  offers,
  filters,
  onFilterChange,
  setFilters,
  selectedOffer,
  onEditOfferClick,
  setApplicationFilters,
}) => {
  const getUniqueOptions = (
    key: keyof Offer,
    splitByComma: boolean = false
  ): { label: string; value: string }[] => {
    const rawValues = offers
      .map((item) => item[key])
      .filter((v): v is string => !!v && v !== "null");

    let values: string[] = [];

    if (splitByComma) {
      values = rawValues
        .flatMap((val) => val.split(","))
        .map((v) => v.trim())
        .filter((v) => v);
    } else {
      values = rawValues.map((v) => v.trim());
    }

    const unique = Array.from(new Set(values));
    return unique.map((val) => ({ label: val, value: val }));
  };

  const brokerIsoOptions = getUniqueOptions("brokerIso");
  const salesRepOptions = getUniqueOptions("salesRep", true);
  const internalRepOptions = getUniqueOptions("internalIsoRep");
  const funderOptions = getUniqueOptions("funder");
  const navigate = useNavigate();

  const isEditDisabled = () => {
    if (selectedOffer?.length != 1) {
      return true;
    }

    const getRowId = (row: any) => {
      const appId = row.dealApplicationId;
      const offerKey = row.offerKey;
      return `${appId}::${offerKey}`;
    };

    const offerToEdit: any = offers.find((offr) => {
      const offerId = getRowId(offr);
      return offerId === selectedOffer[0];
    });

    if (
      offerToEdit?.appStatus?.toLowerCase() === "approved" ||
      offerToEdit?.appStatus?.toLowerCase() === "declined"
    ) {
      return true;
    }
    return false;
  };

  return (
    <Card elevation={3} sx={{ width: "100%", mb: 1, ml: 1 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0,
          }}
        >
          <MDButton
            variant="gradient"
            color="primary"
            disabled={isEditDisabled()}
            onClick={onEditOfferClick}
            sx={{
              height: "40px",
              fontSize: "11px",
              textTransform: "none",
            }}
          >
            Edit Offer
          </MDButton>

          <MDButton
            color="dark"
            disabled={selectedOffer?.length != 1}
            onClick={() => {
              const selected = selectedOffer[0];
              if (selected) {
                const appId = selected.split("::")[0];
                if (appId) {
                  navigate(`/deal_applications/${appId}?source=offers`);
                }
              }
            }}
            sx={{
              height: "40px",
              fontSize: "12px",
              textTransform: "none",
            }}
          >
            View Application
          </MDButton>

          <Tooltip title="Reset All Filters" arrow>
            <IconButton
              size="medium"
              sx={{
                height: "40px",
                width: 40}}
              onClick={() => {
                setFilters({
                  fromDate: null,
                  toDate: null,
                  brokerIso: null,
                  salesRep: null,
                  internalRep: null,
                  funder: null,
                  searchTerm: "",
                  status: null,
                });
                setApplicationFilters({
                  pending: true,
                  funded: false,
                  declined: false,
                });
              }}
            >
              <Icon sx={{ fontSize: 28 }}>filter_alt_off</Icon>
            </IconButton>
          </Tooltip>

          <OfferCustomSelect
            options={brokerIsoOptions}
            label="Broker/ISO"
            value={
              filters.brokerIso ? { label: filters.brokerIso, value: filters.brokerIso } : null
            }
            onChange={(option: any) => onFilterChange("brokerIso", option?.value || null)}
            width={180}
            height={40}
            backgroundColor="#ffffff"
            borderColor="#ccc"
            borderRadius="6px"
            fontSize="14px"
            fontWeight={500}
            textColor="#666666"
            hoverBgColor="#f5f5f5"
            hoverBorderColor="#bdbdbd"
            disableClearable={false}
          />

          <OfferCustomSelect
            options={salesRepOptions}
            label="Sales Rep"
            value={filters.salesRep ? { label: filters.salesRep, value: filters.salesRep } : null}
            onChange={(option: any) => onFilterChange("salesRep", option?.value || null)}
            width={180}
            height={40}
            backgroundColor="#ffffff"
            borderColor="#ccc"
            borderRadius="6px"
            fontSize="14px"
            fontWeight={500}
            textColor="#666666"
            hoverBgColor="#f5f5f5"
            hoverBorderColor="#bdbdbd"
            disableClearable={false}
          />

          <OfferCustomSelect
            options={internalRepOptions}
            label="Internal Rep"
            value={
              filters.internalRep
                ? { label: filters.internalRep, value: filters.internalRep }
                : null
            }
            onChange={(option: any) => onFilterChange("internalRep", option?.value || null)}
            width={180}
            height={40}
            backgroundColor="#ffffff"
            borderColor="#ccc"
            borderRadius="6px"
            fontSize="14px"
            fontWeight={500}
            textColor="#666666"
            hoverBgColor="#f5f5f5"
            hoverBorderColor="#bdbdbd"
            disableClearable={false}
          />

          <OfferCustomSelect
            options={funderOptions}
            label="Funder"
            value={filters.funder ? { label: filters.funder, value: filters.funder } : null}
            onChange={(option: any) => onFilterChange("funder", option?.value || null)}
            height={40}
            backgroundColor="#ffffff"
            borderColor="#ccc"
            borderRadius="6px"
            fontSize="14px"
            fontWeight={500}
            textColor="#666666"
            hoverBgColor="#f5f5f5"
            hoverBorderColor="#bdbdbd"
            disableClearable={false}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <MDDatePicker
              input={{
                placeholder: "Filter From Date",
                sx: {
                  "& .MuiInputBase-root": {
                    height: "40px",
                    maxWidth: "150px",
                    fontSize: "14px",
                    backgroundColor: "#ffffff",
                    color: "#666666",
                    borderRadius: "6px",
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: "#bdbdbd",
                      backgroundColor: "#f5f5f5",
                    },
                    "&.Mui-focused": {
                      borderColor: "#bdbdbd",
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "left",
                      padding: "0 !important",
                    },
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  fontStyle: "italic",
                  fontSize: "14px",
                  color: "#666666",
                },
              }}
              value={filters.fromDate}
              onChange={(date: any) => onFilterChange("fromDate", date)}
            />

            <Box sx={{ ml: 1 }}>
              <MDDatePicker
                input={{
                  placeholder: "Filter To Date",
                  sx: {
                    "& .MuiInputBase-root": {
                      height: "40px",
                      maxWidth: "150px",
                      fontSize: "14px",
                      backgroundColor: "#ffffff",
                      color: "#666666",
                      borderRadius: "6px",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#bdbdbd",
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        borderColor: "#bdbdbd",
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiInputBase-input": {
                        textAlign: "left",
                        padding: "0 !important",
                      },
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontStyle: "italic",
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
                value={filters.toDate}
                onChange={(date: any) => onFilterChange("toDate", date)}
              />
            </Box>
          </Box>

          <MDInput
            placeholder="Search..."
            variant="outlined"
            size="small"
            value={filters.searchTerm}
            onChange={(e: any) => onFilterChange("searchTerm", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#888", fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": {
                height: "40px",
                maxWidth: "150px",
                fontSize: "14px",
                borderRadius: "6px",
                color: "#666666",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#bdbdbd",
                  backgroundColor: "#f5f5f5",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default FilterComponents;
